/**
 * Waiting-list HTTP client.
 *
 * Thin promise wrappers over the shared `data_from_server_post` helper so the
 * components can `await` a `{ ok, data | error }` result instead of juggling
 * success / failure callbacks.  Backend routes live in
 * `backend/app/routes/platform_route.py` (prefix `/platform`).
 */
import { data_from_server_post, get_base_url } from '@/shared/http/client';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorText = (error) => {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error.message === 'string' && error.message.trim()) return error.message;
    return GENERIC_ERROR;
};

const post = (suffix, body) =>
    new Promise((resolve) => {
        data_from_server_post(
            suffix,
            body,
            (data) => resolve({ ok: true, data }),
            (error) => resolve({ ok: false, error: errorText(error) }),
        );
    });

/**
 * Public landing-page submit.  Resolves `{ ok: true, data: { state, created } }`
 * for both a new request (`created: true`) and an email that is already on the
 * list (`created: false`); a cooldown / validation failure resolves
 * `{ ok: false, error }` with the backend's message.
 */
export const joinWaitlist = (email) =>
    post('platform/waitlist/join', { email: String(email || '').trim().toLowerCase() });

/**
 * Authenticated "Done" step: store the four optional answers (`null` = skipped)
 * on the caller's pending request.  Resolves `{ ok: true, data: { request } }`.
 */
export const submitWaitlistAnswers = (answers) =>
    post('platform/waitlist/answers', {
        role: answers?.role ?? null,
        industry: answers?.industry ?? null,
        company_size: answers?.company_size ?? null,
        heard_about: answers?.heard_about ?? null,
    });

/**
 * The caller's platform role and latest Creator request
 * (`{ platform_role, creator_request | null }`), or `null` when the call fails.
 */
export const fetchPlatformMe = async () => {
    try {
        const base = get_base_url(window.location.origin);
        const response = await fetch(`${base}/platform/me`, { credentials: 'include' });
        if (!response.ok) return null;
        return await response.json();
    } catch (_) {
        return null;
    }
};

/**
 * God-only: list Creator requests, newest first.  `status` is one of
 * `pending` / `approved` / `rejected` / `all`.  Resolves the raw array.
 */
export const fetchCreatorRequests = async (status = 'pending') => {
    const base = get_base_url(window.location.origin);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`${base}/platform/creator-requests${query}`, { credentials: 'include' });
    if (!response.ok) {
        let detail = '';
        try {
            const body = await response.json();
            detail = body?.detail || body?.message || '';
        } catch (_) {
            detail = '';
        }
        throw new Error(detail || `Request failed: ${response.status}`);
    }
    const body = await response.json();
    return Array.isArray(body?.requests) ? body.requests : [];
};

/**
 * God-only: move one Creator request to `status` (`approved` / `rejected` /
 * `pending`), also after it was already decided.  The optional note is
 * emailed to the applicant with an approval or rejection; it is ignored for
 * `pending`, which sends no email.
 */
export const setCreatorRequestStatus = (requestId, status, note = null) =>
    post(`platform/creator-requests/${encodeURIComponent(requestId)}/status`, {
        status,
        note: note && String(note).trim() ? String(note).trim() : null,
    });
